import { TokenApi } from '@/api';
import { Call, Device } from '@twilio/voice-sdk';
import { defineStore } from 'pinia';
import { markRaw } from 'vue';

interface PhoneCall {
    origin: string;
    direction: string;
}

interface DialerState {
    ready: boolean;
    dialNumber: string;
    isTwilioDeviceReady: boolean;
    isTwilioDeviceConnected: boolean;
    isIncomingActiveCall: boolean;
    isOutgoingActiveCall: boolean;
    hasIncomingConnection: boolean;
    hasOutgoingConnection: boolean;
    device: Device | undefined;
    message: string;
    currentCall: PhoneCall | undefined;
    incomingCall: PhoneCall | undefined;
    errors: string;
    seconds: number;
    intervalId: any;
    activeConnection: any;
    incomingConnection: any;
}

export const useDialerStore = defineStore('dialer', {
    state: (): DialerState => ({
        ready: false,
        dialNumber: '',
        isTwilioDeviceReady: false,
        isTwilioDeviceConnected: false,
        isIncomingActiveCall: false,
        isOutgoingActiveCall: false,
        hasIncomingConnection: false,
        hasOutgoingConnection: false,
        device: undefined,
        message: '',
        activeConnection: undefined,
        incomingConnection: undefined,
        currentCall: undefined,
        incomingCall: undefined,
        errors: '',
        seconds: 0,
        intervalId: null as ReturnType<typeof setInterval> | null,
    }),
    actions: {
        async onReadyClicked() {
            const api = new TokenApi();
            const result = await api.tokenGet().catch(() => undefined);
            if (result && result.data.data) {
                const jwtToken = result.data.data;
                const device = new Device(jwtToken, {
                    closeProtection: true,
                    codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
                    allowIncomingWhileBusy: true,
                });
                device.on('registered', this.onDeviceReady);
                device.on('error', (error) => {
                    this.errors = error;
                    this.message = '';
                    this.stopTimer();
                });
                device.on('incoming', (connection) => {
                    if (this.incomingConnection) {
                        connection.reject();
                    }
                    this.incomingConnection = markRaw(connection);
                    this.onIncomingConnection(connection.parameters['From']);
                });
                device.on('tokenWillExpire', async () => {
                    const api = new TokenApi();
                    const result = await api.tokenGet().catch(() => undefined);
                    if (result && result.data.data) {
                        const jwtToken = result.data.data;
                        this.device?.updateToken(jwtToken);
                    }
                });

                this.device = markRaw(device);
                this.device.register();
                this.ready = true;
            }
        },
        onDeviceReady() {
            this.isTwilioDeviceReady = true;
        },
        onTwilioDeviceConnected(phoneNumber: string) {
            this.errors = '';
            this.dialNumber = phoneNumber;
            this.isTwilioDeviceConnected = true;
            this.hasIncomingConnection = false;
            this.hasOutgoingConnection = false;

            let name = null;
            if (name) {
                this.message = 'Calling ' + name;
            } else {
                this.message = 'Calling ' + phoneNumber;
            }
        },
        onIncomingConnection(phoneNumber: string) {
            this.isIncomingActiveCall = true;
            this.dialNumber = phoneNumber;
            this.hasIncomingConnection = true;
            this.incomingCall = {
                origin: phoneNumber,
                direction: Call.CallDirection.Incoming,
            };
            this.addCallListeners(this.incomingConnection);
        },
        async startCallWrapper(number: string, conversationSid?: string) {
            if (!this.device) return;
            this.isOutgoingActiveCall = true;
            this.dialNumber = number;
            this.hasOutgoingConnection = true;
            this.currentCall = {
                origin: number,
                direction: Call.CallDirection.Outgoing,
            };
            // When the call originates from a conversation room, forward the
            // ConversationSid so the backend can link the MessageCall to the
            // existing Conversation timeline (#1507). Manual dialer paths omit it.
            const params: Record<string, string> = { To: number };
            if (conversationSid) {
                params.ConversationSid = conversationSid;
            }
            const call = await this.device.connect({ params });
            this.activeConnection = markRaw(call);
            this.addCallListeners(this.activeConnection);
        },
        addCallListeners(call) {
            call.on('accept', (connection) => {
                if (connection.direction === Call.CallDirection.Incoming) {
                    this.currentCall = { ...(this.incomingCall as PhoneCall) };
                    this.onTwilioDeviceConnected(connection.parameters['From']);
                } else {
                    this.onTwilioDeviceConnected(connection.parameters['To']);
                }
                this.startTimer();
            });
            call.on('disconnect', () => {
                if (call.direction === Call.CallDirection.Incoming) {
                    this.isIncomingActiveCall = false;
                    this.hasIncomingConnection = false;
                    this.incomingConnection = undefined;
                } else {
                    this.isOutgoingActiveCall = false;
                    this.hasOutgoingConnection = false;
                }
                this.isTwilioDeviceConnected = false;
                this.message = '';
                this.errors = '';
                this.dialNumber = '';
                if (!this.isActiveCall) {
                    this.stopTimer();
                }
            });
            call.on('cancel', () => {
                if (call.direction === Call.CallDirection.Incoming) {
                    this.isIncomingActiveCall = false;
                    this.hasIncomingConnection = false;
                    this.incomingConnection = undefined;
                } else {
                    this.isOutgoingActiveCall = false;
                    this.hasOutgoingConnection = false;
                }
                this.message = '';
                this.errors = '';
                if (!this.isActiveCall) {
                    this.stopTimer();
                }
            });
            call.on('reject', () => {
                if (call.direction === Call.CallDirection.Incoming) {
                    this.isIncomingActiveCall = false;
                    this.hasIncomingConnection = false;
                    this.incomingConnection = undefined;
                } else {
                    this.isOutgoingActiveCall = false;
                    this.hasOutgoingConnection = false;
                }
                this.message = '';
                this.errors = '';

                if (!this.isActiveCall) {
                    this.stopTimer();
                }
            });
        },
        endCallWrapper() {
            this.activeConnection?.disconnect();
            if (this.isActiveCall) {
                this.stopTimer();
            }
        },
        acceptCallWrapper() {
            if (this.activeConnection) this.activeConnection?.disconnect();
            this.activeConnection = this.incomingConnection;
            this.isIncomingActiveCall = true;
            this.isOutgoingActiveCall = false;
            const call = this.activeConnection;
            call.accept();
            this.hasIncomingConnection = false;
        },
        rejectCallWrapper() {
            this.incomingConnection.reject();
            this.dialNumber = '';
        },
        destroy() {
            this.device?.destroy();
        },
        startTimer() {
            this.stopTimer();
            this.seconds = 0;
            this.intervalId = setInterval(() => {
                this.seconds++;
            }, 1000);
        },
        stopTimer() {
            if (this.intervalId) {
                clearInterval(this.intervalId);
                this.intervalId = null;
                this.seconds = 0;
            }
        },
    },
    getters: {
        isActiveCall: (state) => !!state.isIncomingActiveCall || !!state.isOutgoingActiveCall,
        isIncomingCall: (state) => state.currentCall?.direction == Call.CallDirection.Incoming,
        isOutgoingCall: (state) => state.currentCall?.direction == Call.CallDirection.Outgoing,
        callTimer: (state) => {
            const minutes = Math.floor(state.seconds / 60)
                .toString()
                .padStart(2, '0');
            const seconds = (state.seconds % 60).toString().padStart(2, '0');
            return `${minutes}:${seconds}`;
        },
    },
});
